import { useEffect, useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { codiceInvito, leggiCodiceInvito } from '@/lib/gamehost/rest';
import { useAzioneTavolo } from '@/lib/gamehost/useAzioneTavolo';
import type { GuastoAzione } from '@/lib/gamehost/useAzioneTavolo';
import { LIVELLI_ROBOT, POSTI_1V1, POSTI_2V2, eRobotNonDisponibili } from '@/lib/briscola/gioco';
import type { LivelloRobot, PostiBriscola } from '@/lib/briscola/gioco';
import { briscolaApi } from '@/services/limuloBriscola';

type Azione = 'gioca' | 'invita' | 'entra' | 'robot';
type Guasto = GuastoAzione | 'codiceNonValido' | 'robotNonDisponibili';

/** La frase di un guasto: una chiave di una lista chiusa sotto `hub.gioca.guasto.`. Il testo del server non si mostra MAI. */
function chiaveGuasto(azione: Azione, g: Guasto): string {
  switch (g) {
    case 'non-disponibile':
      return 'nonDisponibile';
    case 'conflitto':
      return azione === 'entra' ? 'conflittoEntra' : 'conflittoCrea';
    case 'nonTrovato':
      return azione === 'entra' ? 'nonTrovato' : 'errore';
    default:
      return g;
  }
}

/**
 * «GIOCA», «CONTRO I ROBOT», «INVITA UN AMICO» ed «ENTRA CON UN CODICE» nella Hub della briscola (sul modello di `GiocaScopa`, lo stesso core): le SCRITTURE, ognuna con la sua
 * chiave di idempotenza (`useAzioneTavolo`: il doppio tocco non crea due tavoli). Prima si sceglie il formato: uno contro uno (2 posti) o due contro due (4 posti, coppie a posti
 * alterni, SOLO amichevole).
 *  · «Gioca»: un tavolo casual; apre `/briscola/tavolo/:id`. Chi completa il tavolo lo decide il game host.
 *  · «Contro i robot»: un tavolo casual col campo `robot: { livello }` (L0 Principiante, L1 Intermedio, L2 Esperto). Chi crea siede a un posto estratto dal core e OGNI altro
 *    posto e un robot del pool, col badge «bot»: nel 2v2 sono tre. Senza account nel pool il tavolo non nasce (503 `no_robot_available`, riconosciuto dallo status E dal codice): si dice in chiaro; un altro 503 resta un guasto di servizio. ⚠️ Il campo `robot` arriva al game host
 *    solo dopo la PR del BFF in QBriscola e il mirror di qlash-limulo#248.
 *  · «Invita un amico»: un tavolo privato; mostra il CODICE da condividere e NON entra da solo (l'invito si vede una volta sola).
 *  · «Entra con un codice»: `briscola.table.join` col `tableId` e l'invito letti dal codice. Un codice che non ha la forma non parte nemmeno.
 * Il codice e una credenziale: sta SOLO nello stato del componente, mai in storage, in log o nell'URL. Nessun tavolo «in classifica»: ranked e tornei sono P9.
 *
 * 🔴 Ogni azione ha una chiave di idempotenza PER CORPO: «Gioca», «Invita» e ognuno dei livelli dei robot, per formato, sono corpi diversi, e una chiave rimasta da un guasto
 * incerto di uno riusata per un altro prende 409 `idempotency_key_reused` dal core. Tornando all'azione di prima si ritrova la SUA chiave: ripetere la stessa azione dopo un esito
 * incerto ritrova lo stesso tavolo, che e il motivo della chiave.
 */
export function GiocaBriscola() {
  const { t } = useTranslation('briscola');
  const navigate = useNavigate();
  const titoloId = useId();
  const [crea] = briscolaApi.useCreateBriscolaTableMutation();
  const [entra] = briscolaApi.useJoinBriscolaTableMutation();
  const aGioca1 = useAzioneTavolo<{ tableId: string }>();
  const aGioca2 = useAzioneTavolo<{ tableId: string }>();
  const aInvita1 = useAzioneTavolo<{ tableId: string; codice: string | null }>();
  const aInvita2 = useAzioneTavolo<{ tableId: string; codice: string | null }>();
  const aRobot = {
    [POSTI_1V1]: { L0: useAzioneTavolo<{ tableId: string }>(), L1: useAzioneTavolo<{ tableId: string }>(), L2: useAzioneTavolo<{ tableId: string }>() },
    [POSTI_2V2]: { L0: useAzioneTavolo<{ tableId: string }>(), L1: useAzioneTavolo<{ tableId: string }>(), L2: useAzioneTavolo<{ tableId: string }>() },
  };
  const aEntra = useAzioneTavolo<{ tableId: string }>();
  const [posti, setPosti] = useState<PostiBriscola>(POSTI_1V1);
  const aGioca = posti === POSTI_1V1 ? aGioca1 : aGioca2;
  const aInvita = posti === POSTI_1V1 ? aInvita1 : aInvita2;
  const [invitoPronto, setInvitoPronto] = useState<{ tableId: string; codice: string | null } | null>(null);
  const [testo, setTesto] = useState('');
  const [codiceStorto, setCodiceStorto] = useState(false);
  const [ultima, setUltima] = useState<Azione | null>(null);
  const [livelloUltimo, setLivelloUltimo] = useState<LivelloRobot>('L0');
  const [senzaRobot, setSenzaRobot] = useState(false);
  const tutte = [aGioca1, aGioca2, aInvita1, aInvita2, aEntra, ...LIVELLI_ROBOT.flatMap((l) => [aRobot[POSTI_1V1][l], aRobot[POSTI_2V2][l]])];
  const occupato = tutte.some((a) => a.stato.invio);

  const vaiAlTavolo = (tableId: string) => navigate(`/briscola/tavolo/${tableId}`);

  const gioca = async () => {
    setUltima('gioca');
    setInvitoPronto(null);
    const r = await aGioca.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'casual', posti, idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId };
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

  const controIRobot = async (livello: LivelloRobot) => {
    setUltima('robot');
    setLivelloUltimo(livello);
    setSenzaRobot(false);
    setInvitoPronto(null);
    const r = await aRobot[posti][livello].esegui(async (idempotencyKey) => {
      try {
        const tavolo = await crea({ tipo: 'casual', posti, idempotencyKey, livello }).unwrap();
        return tavolo === null ? null : { tableId: tavolo.tableId };
      } catch (e) {
        if (eRobotNonDisponibili(e)) setSenzaRobot(true);
        throw e;
      }
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

  const invita = async () => {
    setUltima('invita');
    setInvitoPronto(null);
    const r = await aInvita.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'privato', posti, idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId, codice: tavolo.invito === null ? null : codiceInvito(tavolo.tableId, tavolo.invito) };
    });
    if (r !== null) setInvitoPronto(r);
  };

  const entraCol = async (e: FormEvent) => {
    e.preventDefault();
    setUltima('entra');
    setInvitoPronto(null);
    const letto = leggiCodiceInvito(testo);
    setCodiceStorto(letto === null);
    if (letto === null) return; // niente richiesta per un codice che non ha la forma
    const r = await aEntra.esegui(async (idempotencyKey) => {
      const ingresso = await entra({ tableId: letto.tableId, invito: letto.invito, idempotencyKey }).unwrap();
      return ingresso === null ? null : { tableId: ingresso.tableId };
    });
    if (r !== null) {
      setTesto('');
      vaiAlTavolo(r.tableId);
    }
  };

  const guastoRobot = aRobot[posti][livelloUltimo].stato.guasto;
  const guasto: Guasto | null =
    ultima === 'entra' && codiceStorto
      ? 'codiceNonValido'
      : ultima === 'gioca'
        ? aGioca.stato.guasto
        : ultima === 'invita'
          ? aInvita.stato.guasto
          : ultima === 'robot'
            ? senzaRobot && guastoRobot === 'server'
              ? 'robotNonDisponibili'
              : guastoRobot
            : ultima === 'entra'
              ? aEntra.stato.guasto
              : null;

  return (
    <section data-testid="briscola-gioca" aria-labelledby={titoloId} className="flex shrink-0 flex-col gap-3">
      <h2 id={titoloId} className="text-xs font-bold uppercase tracking-wide text-fg-muted">
        {t('hub.gioca.titolo')}
      </h2>
      <div className="grid grid-cols-2 gap-2" data-scelta-formato>
        <Button variant="scelta" forma="tessera" pressed={posti === POSTI_1V1} data-posti={POSTI_1V1} disabled={occupato} onClick={() => setPosti(POSTI_1V1)}>
          {t('modo.uno')}
        </Button>
        <Button variant="scelta" forma="tessera" pressed={posti === POSTI_2V2} data-posti={POSTI_2V2} disabled={occupato} onClick={() => setPosti(POSTI_2V2)}>
          {t('modo.due')}
        </Button>
      </div>
      <p className="px-1 text-xs text-fg-muted">{posti === POSTI_1V1 ? t('modo.unoNota') : t('modo.dueNota')}</p>
      <Button variant="primary" fullWidth data-azione="gioca" disabled={occupato} onClick={() => void gioca()}>
        {aGioca.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.gioca')}
      </Button>
      <div data-robot className="flex flex-col gap-2">
        <p className="text-sm font-semibold text-fg">{t('hub.gioca.robot.titolo')}</p>
        <div className="grid grid-cols-3 gap-2">
          {LIVELLI_ROBOT.map((livello) => (
            <Button key={livello} variant="secondary" data-azione="robot" data-livello={livello} disabled={occupato} onClick={() => void controIRobot(livello)}>
              {aRobot[posti][livello].stato.invio ? t('hub.gioca.invio') : t(`livello.${livello}`)}
            </Button>
          ))}
        </div>
        <p className="px-1 text-xs text-fg-muted">{posti === POSTI_1V1 ? t('hub.gioca.robot.nota') : t('hub.gioca.robot.notaDue')}</p>
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="secondary" fullWidth data-azione="invita" disabled={occupato} onClick={() => void invita()}>
          {aInvita.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.invita')}
        </Button>
        <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.invitaNota')}</p>
      </div>
      {invitoPronto !== null && <InvitoPronto pronto={invitoPronto} onVai={() => vaiAlTavolo(invitoPronto.tableId)} />}
      <form data-azione="entra" onSubmit={(e) => void entraCol(e)} className="flex flex-col gap-2">
        <label htmlFor={`${titoloId}-codice`} className="text-sm font-semibold text-fg">
          {t('hub.gioca.entra.titolo')}
        </label>
        <input
          id={`${titoloId}-codice`}
          type="text"
          value={testo}
          onChange={(e) => {
            setTesto(e.target.value);
            setCodiceStorto(false);
          }}
          placeholder={t('hub.gioca.entra.segnaposto')}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          disabled={occupato}
          className="min-h-11 w-full min-w-0 rounded-xl border border-border bg-bg-secondary px-3 text-sm text-fg placeholder:text-fg-muted"
        />
        <Button type="submit" variant="secondary" fullWidth disabled={occupato || testo.trim() === ''}>
          {aEntra.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.entra.azione')}
        </Button>
      </form>
      {guasto !== null && (
        <p role="status" data-gioca-guasto={guasto} className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-danger">
          {t(`hub.gioca.guasto.${chiaveGuasto(ultima ?? 'gioca', guasto)}`)}
        </p>
      )}
    </section>
  );
}

/** Il codice da condividere, copiabile. «Copiato» si dice SOLO se la copia e riuscita; a copia fallita il codice si seleziona con un tocco lungo. */
function InvitoPronto({ pronto, onVai }: { pronto: { tableId: string; codice: string | null }; onVai: () => void }) {
  const { t } = useTranslation('briscola');
  const [esito, setEsito] = useState<'ok' | 'ko' | null>(null);
  useEffect(() => setEsito(null), [pronto.codice]);
  const copia = async () => {
    if (pronto.codice === null) return;
    try {
      await navigator.clipboard.writeText(pronto.codice);
      setEsito('ok');
    } catch {
      setEsito('ko');
    }
  };
  return (
    <div data-briscola-invito-pronto className="card-edge flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
      {pronto.codice === null ? (
        <p data-briscola-invito-assente className="text-sm text-fg-muted">
          {t('hub.gioca.codice.assente')}
        </p>
      ) : (
        <>
          <p className="text-sm font-semibold text-fg">{t('hub.gioca.codice.titolo')}</p>
          <code data-briscola-invito-codice className="select-all break-all rounded bg-bg-tertiary px-2 py-2 font-mono text-xs text-fg">
            {pronto.codice}
          </code>
          <p className="text-xs text-fg-muted">{t('hub.gioca.codice.avviso')}</p>
          <Button variant="secondary" fullWidth data-azione="copia" onClick={() => void copia()}>
            {esito === 'ok' ? t('hub.gioca.codice.copiato') : t('hub.gioca.codice.copia')}
          </Button>
          <p role="status" aria-live="polite" className={esito === 'ko' ? 'text-xs text-fg-muted' : 'sr-only'}>
            {esito === 'ko' ? t('hub.gioca.codice.copiaFallita') : esito === 'ok' ? t('hub.gioca.codice.copiato') : ''}
          </p>
        </>
      )}
      <Button variant="primary" fullWidth data-azione="vai" onClick={onVai}>
        {t('hub.gioca.codice.vai')}
      </Button>
    </div>
  );
}
